import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { client } from '../api/client';
import { useCustomFields } from './CustomFieldInputs';
import type { Candidate } from '../types';

/**
 * Everything recorded about one candidate, read-only.
 *
 * A separate panel from the edit dialog rather than a read mode of it. The
 * dialog exists to correct four things and is shaped around that; this exists
 * to answer "who is this person" without the risk of changing them by
 * accident, and the two want opposite layouts.
 *
 * Fetched fresh by id rather than shown from the row already in hand. The list
 * is paginated and filtered, so the row may be minutes old, and a detail view
 * that quietly disagrees with the record is worse than one that takes a moment
 * to load.
 *
 * Empty values read as a dash rather than being hidden. Which fields are blank
 * is itself the answer to "what do we still need from them", and a panel that
 * silently drops them makes a half-filled record look complete.
 */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="w-44 shrink-0 text-xs text-gray-500">{label}</dt>
      <dd className="min-w-0 break-words text-gray-900">
        {value === null || value === undefined || value === '' ? (
          <span className="text-gray-300">—</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function experience(candidate: Candidate): string | null {
  const { experienceMonths, experienceRaw } = candidate;
  if (experienceMonths === null) return experienceRaw;
  if (experienceMonths === 0) return experienceRaw ? `Fresher (${experienceRaw})` : 'Fresher';
  const years = Math.floor(experienceMonths / 12);
  const months = experienceMonths % 12;
  const parts = [years ? `${years}y` : '', months ? `${months}m` : ''].filter(Boolean);
  // The original wording is kept because parsing it into months was lossy.
  return experienceRaw ? `${parts.join(' ')} (${experienceRaw})` : parts.join(' ');
}

export default function CandidateDetailPanel({
  candidateId,
  name,
  onClose,
}: {
  candidateId: string;
  /** Shown while it loads, so the panel is never anonymous. */
  name?: string | null;
  onClose: () => void;
}) {
  const detail = useQuery<Candidate>({
    queryKey: ['candidate', candidateId],
    queryFn: async () =>
      (await client.get(`/api/v1/candidates/${candidateId}`)).data,
    retry: false,
  });

  // The definitions, so a custom value can be shown under its own label
  // rather than as a field id nobody recognises.
  const customFields = useCustomFields('candidate');
  const definitions = customFields.data?.items ?? [];

  const c = detail.data;
  const values = c?.customValues ?? {};

  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {c?.fullName ?? name ?? 'Candidate'}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Everything on file. Use Edit to change any of it.
            </p>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {detail.isLoading ? (
          <p className="px-5 py-8 text-sm text-gray-400">Loading…</p>
        ) : detail.isError || !c ? (
          <p className="px-5 py-8 text-sm text-gray-500">
            That candidate could not be loaded.
          </p>
        ) : (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4 text-sm">
            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Contact
              </h4>
              <dl className="divide-y divide-gray-100">
                <Row label="Mobile" value={c.phone} />
                <Row label="Alternate number" value={c.altPhone} />
                <Row label="Email" value={c.email} />
                <Row label="Date of birth" value={c.dateOfBirth} />
              </dl>
            </section>

            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Background
              </h4>
              <dl className="divide-y divide-gray-100">
                <Row label="Qualification" value={c.qualification} />
                <Row label="Experience" value={experience(c)} />
                <Row label="Designation" value={c.designation} />
                {/* One column, labelled by the flag. Null reads plainly as
                    "Company" rather than claiming either way. */}
                <Row
                  label={
                    c.currentlyEmployed === true
                      ? 'Present company'
                      : c.currentlyEmployed === false
                        ? 'Previous company'
                        : 'Company'
                  }
                  value={c.currentCompany}
                />
                <Row label="Staying location" value={c.currentLocation} />
                <Row label="Preferred location" value={c.preferredLocation} />
              </dl>
            </section>

            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Money and availability
              </h4>
              <dl className="divide-y divide-gray-100">
                <Row label="Present salary" value={c.currentSalary} />
                <Row label="Expected salary" value={c.expectedSalary} />
                <Row
                  label="Notice period"
                  value={c.noticePeriodDays === null ? null : `${c.noticePeriodDays} days`}
                />
              </dl>
            </section>

            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Resume
              </h4>
              <dl className="divide-y divide-gray-100">
                {/* Both, because they answer different questions and can
                    disagree — a promise kept without a file attached, or a
                    file attached that was never promised. */}
                <Row
                  label="File on record"
                  value={
                    c.hasResume ? (
                      <span className="text-emerald-700">Yes</span>
                    ) : (
                      <span className="text-gray-500">No file uploaded</span>
                    )
                  }
                />
                <Row label="Promised by" value={c.resumeDueAt} />
                <Row label="Marked received" value={c.resumeReceivedAt} />
              </dl>
            </section>

            {definitions.length > 0 && (
              <section>
                <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                  Additional fields
                </h4>
                <dl className="divide-y divide-gray-100">
                  {definitions.map((field) => (
                    <Row
                      key={field.id}
                      label={field.label}
                      value={values[field.id] ?? null}
                    />
                  ))}
                </dl>
              </section>
            )}

            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Other
              </h4>
              <dl className="divide-y divide-gray-100">
                <Row label="Source" value={c.source} />
                <Row label="Latest call client" value={c.latestCallClientName} />
                <Row label="Notes" value={c.notes} />
              </dl>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}
